import { Box, Skeleton, Flex } from "@chakra-ui/react";
import NextImage from "next/legacy/image";
import React, { useEffect, useState } from "react";
import { storage } from "../../src/config/firebase";
import {
  RelevantImg,
  RelevantListingBox,
  SubjectLine,
  RelevantTypeBadge,
  RelevantOffMarketBadge,
  RelevantPrice,
  RelevantDetailLine,
} from "./ListingCardStyles";
import NumberFormat from "react-number-format";
import * as Sentry from '@sentry/nextjs'
import { getDownloadURL, listAll, ref } from "firebase/storage";
import { clientError } from "../../utils/utilties";
import { provinceShorter } from "./ListingCard2point0";

const RelevantListing = (props) => {
    const [imageLoadStatus, setImageLoadStatus] = useState('loading');
    const {
        price, priceType, type, city, province, subject, id, primaryImg,
        imageOrder, imagesFresh, imageURLs,
        propertyTypes, subTypes, businessType, businessCategory,
        totalBuildingSize, lotSize, lotSizeUnit,
        grossRevenue, unpriced, offMarket,
    } = props;
    const [imageURL, setImageURL] = useState(imagesFresh && imageURLs?.length > 0 ? imageURLs[0] : null)

    String.prototype['toProperCase'] = function () {
        return this.replace(/\w\S*/g, function (txt) { return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase(); });
    };

    const functionv2 = async () => {
        if (imageURLs?.length > 0 && imagesFresh) {
            setImageURL(imageURLs[0])
        }

        else {

            const listingRef = ref(storage, `listingImages/${id}/compressed`);
            let periodIndex = primaryImg?.lastIndexOf(".")
            let primaryImageName = primaryImg?.substring(0, periodIndex);
            let primaryImageExt = primaryImg?.substring(periodIndex);

            setImageLoadStatus('loading')

            let returnedFiles = await listAll(listingRef);
            let returnedItems = returnedFiles?.items

            if (returnedItems?.length > 0) {

                if (imageOrder?.length > 0) {

                    let f = imageOrder[0];

                    returnedItems.filter(it => {
                        const nameRef = ref(storage, `listingImages/${id}/compressed/${it.name}`)
                        if (it.name.split(/\.(?=[^\.]+$)/)[0] === f?.split(/\.(?=[^\.]+$)/)[0] + '_700x700' || it.name.split(/\.(?=[^\.]+$)/)[0] === f?.split(/\.(?=[^\.]+$)/)[0]) {
                            getDownloadURL(nameRef).then(url => {
                                setImageURL(url)
                                setImageLoadStatus('success')
                            }).catch(err => {

                                clientError(err)
                                setImageLoadStatus('success')
                                setImageURL("/images/placeholder-image.svg")
                            })
                        }
                    })
                }

                else {
                    let imagePromises = returnedItems.reduce((accumulator, currentValue) => {
                        if (currentValue.name.includes("_700x700")) {

                            if (currentValue.name == `${primaryImageName}_700x700${primaryImageExt}`) {
                                getDownloadURL(currentValue).then(url => {
                                    setImageURL(url)
                                    setImageLoadStatus('success')
                                }).catch(err => {
                                    clientError(err)
                                    setImageLoadStatus('success')
                                    setImageURL("/images/placeholder-image.svg")
                                })
                            }

                        }
                        return accumulator;
                    }, []);

                }
            }

            else {
                setImageURL("/images/placeholder-image.svg")
                setImageLoadStatus('success')
                return
            }
        }

    }

    useEffect(() => {
        if (!imageURL) {
            functionv2();
        }
    }, [id])

    const isBusiness = propertyTypes?.includes("Sale of Business");
    const isLand = propertyTypes?.includes("Land");
    const isCommercial = propertyTypes?.some((t: string) => ["Office", "Industrial", "Retail"].includes(t));

    // Most specific type label: businessType > businessCategory > subTypes[0] > propertyTypes[0]
    const typeLabel = businessType || businessCategory || subTypes?.[0] || propertyTypes?.[0] || null;

    let detailLine: string | null = null;
    if (isBusiness && grossRevenue) {
        detailLine = `Est. Revenue: $${Number(grossRevenue).toLocaleString()}`;
    } else if (isLand && lotSize) {
        detailLine = `${Number(lotSize).toLocaleString()} ${lotSizeUnit || 'acres'}`;
    } else if (isCommercial && totalBuildingSize) {
        detailLine = `${Number(totalBuildingSize).toLocaleString()} sq ft`;
    }

    const locationText = [city, province ? provinceShorter(province) : null].filter(Boolean).join(", ");

    return (
        (<RelevantListingBox>
            <RelevantImg>
                {imageURL ? (
                    <NextImage
                        layout="fill"
                        objectFit="cover"
                        src={imageURL}
                        alt={subject}
                    />
                ) : (
                    <Skeleton
                        position="absolute"
                        inset="0"
                        height="100%"
                        width="100%"
                    />
                )}
            </RelevantImg>

            <Box p="10px 12px 11px" minHeight="130px" display="flex" flexDirection="column">
                {(typeLabel || offMarket) && (
                    <Flex alignItems="center" flexWrap="wrap" gap="4px" mb="7px">
                        {typeLabel && (
                            <RelevantTypeBadge>{typeLabel}</RelevantTypeBadge>
                        )}
                        {offMarket && (
                            <RelevantOffMarketBadge>Off Market</RelevantOffMarketBadge>
                        )}
                    </Flex>
                )}

                <SubjectLine>
                    {subject?.toProperCase()}
                </SubjectLine>

                {locationText && (
                    <Box
                        color="#9ca3af"
                        fontSize="11px"
                        fontWeight="500"
                        letterSpacing="0.4px"
                        textTransform="uppercase"
                        mt="2px"
                    >
                        {locationText}
                    </Box>
                )}

                <RelevantPrice>
                    {unpriced ? (
                        <Box as="span" fontSize="13px" color="#9ca3af" fontWeight="500">Price on Request</Box>
                    ) : type === 'For Sale' ? (
                        <NumberFormat value={price} displayType={'text'} thousandSeparator={true} prefix={'$'} />
                    ) : type === 'For Lease' ? (
                        <NumberFormat value={price} displayType={'text'} thousandSeparator={true} prefix={'$'} suffix={'/' + priceType} />
                    ) : null}
                    {!unpriced && type && (
                        <Box as="span" fontSize="11px" color="#9ca3af" fontWeight="400" ml="5px">
                            · {type}
                        </Box>
                    )}
                </RelevantPrice>

                {detailLine && (
                    <RelevantDetailLine>{detailLine}</RelevantDetailLine>
                )}
            </Box>
        </RelevantListingBox>)
    );
};

export default RelevantListing;
